import React, { useState, useEffect } from 'react';
import { Sparkles, Pencil, RefreshCw, Save, X, AlertCircle, Info, Linkedin, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import SkillSelector from './SkillSelector';
import { supabase } from '../../../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * MatchProfileCard - Display and edit AI-generated match profile for an RFQ
 *
 * Props:
 * - rfqId: UUID of the RFQ
 * - onProfileChange: Callback when profile is updated (optional)
 */
const MatchProfileCard = ({ rfqId, onProfileChange }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [skillsMap, setSkillsMap] = useState({});
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = (field, text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // Edit form state
  const [editForm, setEditForm] = useState({
    min_clearance_level: 0,
    clearance_requirement_type: 'ability_to_obtain',
    required_skill_ids: [],
    nice_to_have_skill_ids: [],
    target_locations: [],
    min_experience_level: 'beginner'
  });

  useEffect(() => {
    fetchProfile();
  }, [rfqId]);

  useEffect(() => {
    if (profile && (profile.required_skill_ids?.length > 0 || profile.nice_to_have_skill_ids?.length > 0)) {
      fetchSkillNames();
    }
  }, [profile]);

  const fetchSkillNames = async () => {
    try {
      const allSkillIds = [
        ...(profile.required_skill_ids || []),
        ...(profile.nice_to_have_skill_ids || [])
      ];

      if (allSkillIds.length === 0) return;

      const { data: skills, error } = await supabase
        .from('skills_master')
        .select('id, name')
        .in('id', allSkillIds);

      if (error) {
        console.error('Error fetching skill names:', error);
        return;
      }

      const map = {};
      skills.forEach(skill => {
        map[skill.id] = skill.name;
      });
      setSkillsMap(map);
    } catch (err) {
      console.error('Error fetching skill names:', err);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/matching/rfq/${rfqId}/profile`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProfile(data.profile);
        // Initialize edit form with current values
        setEditForm({
          min_clearance_level: data.profile.min_clearance_level || 0,
          clearance_requirement_type: data.profile.clearance_requirement_type || 'ability_to_obtain',
          required_skill_ids: data.profile.required_skill_ids || [],
          nice_to_have_skill_ids: data.profile.nice_to_have_skill_ids || [],
          target_locations: data.profile.target_locations || [],
          min_experience_level: data.profile.min_experience_level || 'beginner'
        });
      } else if (response.status === 404) {
        // No profile yet - that's okay
        setProfile(null);
      } else {
        throw new Error(data.message || 'Failed to fetch profile');
      }
    } catch (err) {
      console.error('Error fetching match profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProfile = async (forceRegenerate = false) => {
    try {
      setGenerating(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/matching/rfq/${rfqId}/generate-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ forceRegenerate })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProfile(data.profile);
        if (onProfileChange) onProfileChange(data.profile);
      } else {
        throw new Error(data.message || 'Failed to generate profile');
      }
    } catch (err) {
      console.error('Error generating profile:', err);
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/matching/rfq/${rfqId}/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProfile(data.profile);
        setEditing(false);
        if (onProfileChange) onProfileChange(data.profile);
      } else {
        throw new Error(data.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form to current profile values
    setEditForm({
      min_clearance_level: profile?.min_clearance_level || 0,
      clearance_requirement_type: profile?.clearance_requirement_type || 'ability_to_obtain',
      required_skill_ids: profile?.required_skill_ids || [],
      nice_to_have_skill_ids: profile?.nice_to_have_skill_ids || [],
      target_locations: profile?.target_locations || [],
      min_experience_level: profile?.min_experience_level || 'beginner'
    });
    setEditing(false);
    setError(null);
  };

  const clearanceLevelToText = (level) => {
    const map = { 0: 'None', 1: 'Baseline', 2: 'NV1', 3: 'NV2', 4: 'TSPV' };
    return map[level] || 'None';
  };

  const experienceLevelToText = (level) => {
    const map = {
      'beginner': 'Beginner',
      'intermediate': 'Intermediate',
      'professional': 'Professional',
      'expert': 'Expert'
    };
    return map[level] || level;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              AI Match Profile
              {profile && profile.ai_prompt_used && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setShowDebugModal(true)}
                  title="View AI prompt and response"
                >
                  <Info className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </Button>
              )}
            </CardTitle>
            <CardDescription>
              {profile
                ? 'AI-generated matching criteria, used by the Candidate Matches search below, plus LinkedIn sourcing material'
                : 'Analyses this role to find matching candidates below and generate LinkedIn sourcing material'
              }
            </CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            {profile && !editing && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerateProfile(true)}
                  disabled={generating}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
              </>
            )}
            {!profile && (
              <Button
                onClick={() => handleGenerateProfile(false)}
                disabled={generating}
              >
                <Sparkles className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                Generate Profile
              </Button>
            )}
            {editing && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={saving}
                >
                  <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert className="mb-4 bg-red-50 border-red-200">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {generating && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Analyzing RFQ with AI...</p>
          </div>
        )}

        {!generating && !profile && (
          <div className="text-center py-8">
            <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No match profile generated yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Click "Generate Profile" to analyze this RFQ with AI
            </p>
          </div>
        )}

        {!generating && profile && !editing && (
          <div className="space-y-6">
            {/* Clearance */}
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Security Clearance</p>
              <div className="flex items-center space-x-2">
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {clearanceLevelToText(profile.min_clearance_level)}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {profile.clearance_requirement_type === 'required' ? 'Required' : 'Ability to Obtain'}
                </Badge>
              </div>
            </div>

            {/* Required Skills */}
            {profile.required_skill_ids && profile.required_skill_ids.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Required Skills</p>
                <div className="flex flex-wrap gap-2">
                  {profile.required_skill_ids.map((skillId, idx) => (
                    <Badge key={idx} className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {skillsMap[skillId] || `Skill ${skillId.substring(0, 8)}`}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Nice-to-Have Skills */}
            {profile.nice_to_have_skill_ids && profile.nice_to_have_skill_ids.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Nice-to-Have Skills</p>
                <div className="flex flex-wrap gap-2">
                  {profile.nice_to_have_skill_ids.map((skillId, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {skillsMap[skillId] || `Skill ${skillId.substring(0, 8)}`}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Locations */}
            {profile.target_locations && profile.target_locations.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Target Locations</p>
                <div className="flex flex-wrap gap-2">
                  {profile.target_locations.map((loc, idx) => (
                    <Badge key={idx} variant="outline">
                      {loc}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Experience Level */}
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Minimum Experience Level</p>
              <Badge variant="outline">
                {experienceLevelToText(profile.min_experience_level)}
              </Badge>
            </div>

            {/* LinkedIn Outreach - manual-copy sourcing material, nothing auto-sent */}
            {(profile.linkedin_search_terms || profile.linkedin_connection_message || profile.linkedin_inmail_message) && (
              <div className="border-t pt-6 space-y-4">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-[#0077B5]" />
                  LinkedIn Outreach
                </p>
                <p className="text-xs text-muted-foreground -mt-2">
                  For sourcing candidates directly on LinkedIn - copy and use yourself, nothing is sent automatically.
                </p>

                {profile.linkedin_search_terms && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-muted-foreground">Key Search Terms</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2"
                        onClick={() => handleCopy('search_terms', profile.linkedin_search_terms)}
                      >
                        {copiedField === 'search_terms' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                    <p className="text-sm bg-muted rounded-md p-2">{profile.linkedin_search_terms}</p>
                  </div>
                )}

                {profile.linkedin_connection_message && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-muted-foreground">
                        Connection Message ({profile.linkedin_connection_message.length}/300 chars)
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2"
                        onClick={() => handleCopy('connection_message', profile.linkedin_connection_message)}
                      >
                        {copiedField === 'connection_message' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                    <p className="text-sm bg-muted rounded-md p-3 whitespace-pre-wrap">{profile.linkedin_connection_message}</p>
                  </div>
                )}

                {profile.linkedin_inmail_message && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-muted-foreground">InMail Message</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2"
                        onClick={() => handleCopy('inmail_message', profile.linkedin_inmail_message)}
                      >
                        {copiedField === 'inmail_message' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                    <p className="text-sm bg-muted rounded-md p-3 whitespace-pre-wrap">{profile.linkedin_inmail_message}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!generating && editing && (
          <div className="space-y-6">
            {/* Clearance Edit */}
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-2">
                Security Clearance
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Minimum Level</label>
                  <select
                    value={editForm.min_clearance_level}
                    onChange={(e) => setEditForm({ ...editForm, min_clearance_level: parseInt(e.target.value) })}
                    className="w-full border rounded-md px-3 py-2 bg-background"
                  >
                    <option value={0}>None</option>
                    <option value={1}>Baseline</option>
                    <option value={2}>NV1</option>
                    <option value={3}>NV2</option>
                    <option value={4}>TSPV</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Requirement Type</label>
                  <select
                    value={editForm.clearance_requirement_type}
                    onChange={(e) => setEditForm({ ...editForm, clearance_requirement_type: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 bg-background"
                  >
                    <option value="ability_to_obtain">Ability to Obtain</option>
                    <option value="required">Required</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Skills Edit */}
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-2">
                Required Skills
              </label>
              <SkillSelector
                selectedSkillIds={editForm.required_skill_ids}
                onChange={(skillIds) => setEditForm({ ...editForm, required_skill_ids: skillIds })}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-2">
                Nice-to-Have Skills
              </label>
              <SkillSelector
                selectedSkillIds={editForm.nice_to_have_skill_ids}
                onChange={(skillIds) => setEditForm({ ...editForm, nice_to_have_skill_ids: skillIds })}
              />
            </div>

            {/* Locations Edit */}
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-2">
                Target Locations
              </label>
              <input
                type="text"
                value={editForm.target_locations.join(', ')}
                onChange={(e) => setEditForm({
                  ...editForm,
                  target_locations: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                })}
                placeholder="Enter locations separated by commas"
                className="w-full border rounded-md px-3 py-2 bg-background"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Example: Canberra, Sydney, Melbourne
              </p>
            </div>

            {/* Experience Level Edit */}
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-2">
                Minimum Experience Level
              </label>
              <select
                value={editForm.min_experience_level}
                onChange={(e) => setEditForm({ ...editForm, min_experience_level: e.target.value })}
                className="w-full border rounded-md px-3 py-2 bg-background"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="professional">Professional</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>
        )}
      </CardContent>

      {/* AI Debug Modal */}
      <Dialog open={showDebugModal} onOpenChange={setShowDebugModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AI Match Profile Debug Info</DialogTitle>
            <DialogDescription>
              View the AI prompt and raw response used to generate this match profile
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* AI Prompt */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center">
                <Sparkles className="h-4 w-4 mr-2 text-indigo-600" />
                Prompt Sent to AI
              </h3>
              <div className="bg-muted p-4 rounded-md">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {profile?.ai_prompt_used || 'No prompt data available'}
                </pre>
              </div>
            </div>

            {/* AI Raw Response */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 text-blue-600" />
                Raw AI Response
              </h3>
              <div className="bg-muted p-4 rounded-md">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {profile?.raw_ai_output
                    ? JSON.stringify(profile.raw_ai_output, null, 2)
                    : 'No response data available'}
                </pre>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MatchProfileCard;
